import { runInAction, toJS, makeAutoObservable } from 'mobx';
import { DropdownOption, Permit, Rider, Vehicle, Workdest, RiderFormValues, VehicleFormValues } from '../models/permit';
import { store } from "./store";
import agent from '../api/agent';
import { getMondayOfNextWeek, setPermitProps, setVehicleOrderSeq } from '../common/util/util';
import { toast } from 'react-toastify';
import { DropDownOptions, Terminal } from '../models/form';
import { router } from '../router/Routes';

// const LIMIT = 2;

export default class PermitStore {
    userPermitRegistry = new Map<number, Permit>();
    permitRegistry = new Map<number, Permit>();
    permit: Permit | null = null;
    tempPermit: Permit = {} as Permit;
    loadingInitial = false;
    submitting = false;
    loading = false;
    loadingPermits = false;
    step = 0;
    routeRegistry = new Map<string, DropDownOptions>();
    routeScheduleTimeRegistry = new Map();
    routeTerminalRegistry = new Map();
    terminalRegistry = new Map<number, Terminal>();
    amTimeRegistry = new Map();
    pmTimeRegistry = new Map();
    permitCount = 0;
    page = 0;
    
    constructor() {
        makeAutoObservable(this);
    }

    setPermit = (permit: Permit) => {
        runInAction(() => {
            if(this.permit) {
                this.permit = permit;
            }
        });
    }

    setTempPermit = (permit: Permit) => {
        runInAction(()=> {
            if(this.tempPermit) {
                this.tempPermit = permit;
            }
        });
    }

    cancelVehicles = () => {
        this.setTempPermit({
            ...this.permit!,
            vehicles: this.permit!.vehicles
        });
    }

    cancelRiders = () => {
        this.setTempPermit({
            ...this.permit!,
            riders: this.permit!.riders
        });
    }
    
    getRouteName = (id: number) => {
        return this.routeRegistry.get(`${id}`)?.text;
    }

    getTerminalName = (id: string) => {
        return this.routeTerminalRegistry.get(`${id}`)?.text;
    }

    getPermit = (id: string) => {
        return this.permitRegistry.get(+id)!;
    }

    get amTimeOptions() {
        return Array.from(this.amTimeRegistry.values());
    }

    get pmTimeOptions() {
        return Array.from(this.pmTimeRegistry.values());
    }

    getAmTimeByDay = (id: number) => {
        return this.amTimeRegistry.get(id);
    }

    getPmTimeByDay = (id: number) => {
        return this.pmTimeRegistry.get(id);
    }

    // get permitsByDate() {
    //     return Array.from(this.permitRegistry.values()).sort((a, b) =>
    //         new Date(b.dateWeekBegin!).getTime() - new Date(a.dateWeekBegin!).getTime());
    // }

    get permits() {
        return Array.from(this.permitRegistry.values());
    }

    get routes() {
        return Array.from(this.routeRegistry.values());
    }

    get terminals() {
        return Array.from(this.terminalRegistry.values());
    }

    get weekBegin() {
        return getMondayOfNextWeek();
    }

    loadPermit = async (id: string) => {
        let newPermit = this.getPermit(id);
        
        if (newPermit) {
            this.permit = newPermit;
            this.tempPermit.vehicles = newPermit.vehicles;
            this.tempPermit.riders = newPermit.riders;

            this.loadRouteTerminals(+newPermit!.routeId);
            this.loadRouteScheduleTimes(`${newPermit!.routeId}`, +newPermit!.amTerminalId);
            
            return toJS(newPermit);
        } else {
            this.loadingInitial = true;
            try {
                newPermit = await agent.Permits.details(id);
                
                if(this.routeRegistry.size < 1)
                    this.loadRoutes();

                this.loadRouteTerminals(+newPermit!.routeId);
                this.loadRouteScheduleTimes(`${newPermit!.routeId}`, +newPermit!.amTerminalId);

                runInAction(() => {
                    setPermitProps(newPermit);
                    this.permit = newPermit;
                    this.tempPermit.vehicles = newPermit.vehicles;
                    this.tempPermit.riders = newPermit.riders;

                    this.permitRegistry.set(newPermit.permitId, newPermit); // set data in registry
                    this.loadingInitial = false;
                })
                return newPermit;
            } catch (error) {
                runInAction(() => {
                    this.loadingInitial = false;
                })
                // console.log(error);
                router.navigate(`${process.env.PUBLIC_URL}/notfound`);
            }
        }
    }

    loadRoutes = async () => {
        this.loadingInitial = true;
        try {
            const permitsEnvelope = await agent.Permits.loadRoutes();
            const { routeOptions } = permitsEnvelope;
            
            runInAction(()=> {
                // this.routeRegistry.clear();
                routeOptions.forEach(route => {
                    this.routeRegistry.set(route.key, route);
                });
                this.loadingInitial = false;
            });
        } catch (error) {
            runInAction(()=> {
                this.loadingInitial = false;
            });
            // console.log(error);
            router.navigate(`${process.env.PUBLIC_URL}`);
        }
    }
    
    loadTerminals = async () => {
        this.loadingInitial = true;
        try {
            const terminals = await agent.Permits.loadTerminals();
            runInAction(()=> {
                this.terminalRegistry.clear();
                terminals.forEach(terminal => {
                    this.terminalRegistry.set(terminal.terminal_Id, terminal);
                });
                this.loadingInitial = false;
            });
        } catch (error) {
            runInAction(()=> {
                this.loadingInitial = false;
            });
            // console.log(error);
            router.navigate(`${process.env.PUBLIC_URL}`);
        }
    }

    redirectTo = (path: string) => {
        router.navigate(`${process.env.PUBLIC_URL}/${path}`);
    }
    
    loadRouteTerminals = async (routeId: number) => {
        this.loadingInitial = true;
        try {
            const permitsEnvelope = await agent.Permits.loadRouteTerminals(routeId);
            const { terminals } = permitsEnvelope;
            
            runInAction(()=> {
                this.routeTerminalRegistry.clear();
                terminals.forEach(terminal => {
                    if(!this.routeTerminalRegistry.has(terminal.key))
                        this.routeTerminalRegistry.set(terminal.key, terminal);
                });
                this.loadingInitial = false;
            });
        } catch (error) {
            runInAction(()=> {
                this.loadingInitial = false;
            });
            // console.log(error);
            router.navigate(`${process.env.PUBLIC_URL}`);
        }
    }

    loadRouteScheduleTimes = async (routeId: string, amTerminalid: number) => { 
        this.loadingInitial = true;
        try {
            const times = await agent.Permits.loadRouteScheduleTimes(+routeId, amTerminalid);

            runInAction(()=> {
                this.amTimeRegistry.clear();
                times.amRouteTimes.forEach(d => {
                    this.amTimeRegistry.set(d.day, d.times);
                });

                this.pmTimeRegistry.clear();
                times.pmRouteTimes.forEach(d => {
                    this.pmTimeRegistry.set(d.day, d.times);
                });
                
                this.loadingInitial = false;
            });
        } catch (error) {
            runInAction(()=> {
                this.loadingInitial = false;
            });
            // console.log(error);
        }
    }

    get routeTerminals(): DropdownOption[] {
        return Array.from(this.routeTerminalRegistry.values());
    }

    createPermit = async (permit: any) => { //Permit
        this.submitting = true;
        try {
            // input uses string otherwise it won't save instancely
            permit.routeId = +permit.routeId; // need to cast due to the input makes string 
            permit.amTerminalId = +permit.amTerminalId; // need to cast due to the input makes string 
            permit.pmTerminalId = +permit.pmTerminalId; // need to cast due to the input makes string 

            await agent.Permits.create(permit);
            
            toast.success('New permit has been added');

            runInAction(()=> {
                this.userPermitRegistry.set(permit.id, permit);
                this.submitting = false;
            });
            router.navigate(`${process.env.PUBLIC_URL}/permits`);
        } catch (error) {
            runInAction(()=> {
                this.submitting = false;
            });
            toast.error('Problem submitting data');
            // router.navigate(`/permits`);
        }
    }

    // 21-3
    increaseStep = () => {
        this.step = this.step + 1;
    }

    decreaseStep = () => {
        this.step = this.step - 1;
    }

    clickStep = (step: number) => {
        this.step = step;
    }

    addTempVehicle = (vehicle: any) => {
        runInAction(()=> {
            this.setTempPermit({
                ...this.tempPermit,
                vehicles: [...this.tempPermit.vehicles, vehicle]
            });
        });
    }
    addTempRider = (rider: any) => {
        runInAction(()=> {
            this.setTempPermit({
                ...this.tempPermit,
                riders: [...this.tempPermit.riders, rider]
            });
        });
    }

    updateTempRider = (formValues: RiderFormValues, rider: Rider, type: string) => {
        runInAction(()=> {
            let modifiedRiders = this.tempPermit.riders.map(x => { return Object.assign({}, x)});
            let modifiedRider = modifiedRiders.find(x => x.permitRiderId === rider.permitRiderId)!;
            modifiedRider.firstName = formValues.firstName;
            modifiedRider.lastName = formValues.lastName;
            modifiedRider.address = formValues.address;
            modifiedRider.address2 = formValues.address2;
            modifiedRider.city = formValues.city;
            modifiedRider.zipcode = formValues.zipcode;
            modifiedRider.phone = formValues.phone;

            modifiedRider.employerName = formValues.employerName;
            modifiedRider.employerCity = formValues.employerCity;
            modifiedRider.employerZipcode = formValues.employerZipcode;
            
            this.tempPermit.riders = modifiedRiders;

            // close modals if opened
            if(type === 'edit') {
                store.modalStore.closeModal2();
            }
            if(type === 'new') {
                store.modalStore.closeModal();
            }
        });
    }
    
    updateTempVehicle = (formValues: VehicleFormValues, vehicle: Vehicle, type: string) => {
        runInAction(()=> {
            let modifiedVehicles = this.tempPermit.vehicles.map(x => { return Object.assign({}, x)});
            let modifiedVehicle = modifiedVehicles.find(x => x.permitVehicleId === vehicle.permitVehicleId)!;
            modifiedVehicle.make = formValues.make;
            modifiedVehicle.model = formValues.model;
            modifiedVehicle.year = +formValues.year; // input converted to string so needs to cast to number
            modifiedVehicle.licensePlateNbr = formValues.licensePlateNbr;
            modifiedVehicle.vehicleIsPrimary = formValues.vehicleIsPrimary;
            modifiedVehicle.hovTransitVehicleNbr = formValues.hovTransitVehicleNbr;
            modifiedVehicle.transitAgency = formValues.transitAgency;
            
            this.tempPermit.vehicles = modifiedVehicles;

            // close modals if opened
            if(type === 'edit') {
                store.modalStore.closeModal2();
            }
            if(type === 'new') {
                store.modalStore.closeModal();
            }
        });
    }

    removeVehicle = (i: number) => {
        runInAction(()=> {
            let vehicles = this.tempPermit.vehicles.map(x => { return Object.assign({}, x)});
            vehicles.splice(i, 1);
            
            // set vehicle order seq when deleting
            // when there is only one, then set as primary vehicle
            const findPrimaryIndex = vehicles.findIndex(x => x.vehicleSeqNbr === 1);
            const primaryIndex = findPrimaryIndex >= 0 ? findPrimaryIndex : 0;
            
            if(vehicles.length === 1) {
                vehicles[0].vehicleIsPrimary = true;
            }
            vehicles = setVehicleOrderSeq(vehicles, primaryIndex);

            this.tempPermit.vehicles = vehicles;
        });
    }
    
    setPrimaryVehicle = (i: number) => {
        runInAction(()=> {
            let vehicles = this.tempPermit.vehicles.map(x => { return Object.assign({}, x)});

            let currentPrimaryIndex = vehicles.findIndex(x => x.vehicleSeqNbr === 1)!;
            vehicles[currentPrimaryIndex].vehicleIsPrimary = false;
            vehicles[i].vehicleIsPrimary = true;
            
            setVehicleOrderSeq(vehicles, i);
            
            this.tempPermit.vehicles = vehicles;
        });
    }
    
    removeRider = (i: number) => {
        runInAction(()=> {
            let riders = this.tempPermit.riders.map(x => { return Object.assign({}, x)});
            riders.splice(i, 1);
            
            this.tempPermit.riders = riders;
        });
    }

    // loadPermits require loadRoutes
    loadPermits = async () => {
        this.loadingPermits = true;
        try {
            const { permits } = await agent.Permits.filterList(store.tabStore.tab.predicate);

            // initialize
            if(this.routeRegistry.size < 1)
                this.loadRoutes();
                
            runInAction(()=> {
                permits.forEach(permit => {
                    setPermitProps(permit);
                    this.permitRegistry.set(permit.permitId, permit);
                });
                this.loadingPermits = false;
            });
        } catch (error) {
            toast.error('Problem loading permits');
            runInAction(()=> {
                this.loadingPermits = false;
            });
            // console.log(error);
        }
    }

    editWorkdest = async (workdest: Workdest, permitId: number) => {
        try {
            await agent.Permits.updateWorkdest(workdest, permitId);
            this.setPermit({
                ...this.permit!,
                workdestCompany: workdest.workdestCompany,
                workdestAddress: workdest.workdestAddress,
                workdestAddress2: workdest.workdestAddress2,
                workdestCity: workdest.workdestCity,
                workdestState: workdest.workdestState,
                workdestZipcode: workdest.workdestZipcode,
            });
            
            store.modalStore.closeModal();
            toast.success('The work destination info have been updated.');
        } catch (error) {
          toast.error('Problem updating work destination');
          router.navigate(`${process.env.PUBLIC_URL}`);
        }
    }

    editVehicles = async () => {
        try {
            await agent.Permits.updateVehicles(this.tempPermit.vehicles, this.permit!.permitId);
            
            this.setPermit({
                ...this.permit!,
                vehicles: this.tempPermit.vehicles
            });

            store.modalStore.closeModal();
            toast.success('The vehicle info have been updated.');
        } catch (error) {
            toast.error('Problem updating vehicles');
            router.navigate(`${process.env.PUBLIC_URL}`);
        }
    }

    editRiders = async (riders: Rider[]) => {
        // add/update/delete
        try {
            await agent.Permits.updateTempRider(this.tempPermit.riders, this.permit!.permitId);

            this.setPermit({
                ...this.permit!,
                riders: this.tempPermit.riders
            });

            store.modalStore.closeModal();
            toast.success('The rider info have been updated.');
        } catch (error) {
            toast.error('Problem updating riders');
            router.navigate(`${process.env.PUBLIC_URL}`);
        }
    }

    // clearSelectedPermit = () => {
    //     this.selectedPermit = undefined;
    // }
}